import { useCallback, useEffect, useState } from 'react';

import type { ConversionJob } from '../store/hooks';
import {
  useLaunchMigrationConversionMutation,
  useLookupMigrationConversionsMutation,
} from '../store/hooks';
import { type LaunchRefusal, readLaunchRefusal } from '../utils/launch-refusal';

/** Poll durable worker state in bounded batches, discarding responses after scope changes. */
export function useMigrationConversions(organizationId: string, rawBatchIds: string[]) {
  const [lookup] = useLookupMigrationConversionsMutation();
  const [launch] = useLaunchMigrationConversionMutation();
  const [version, setVersion] = useState(0);
  const [state, setState] = useState<{ scope: string; jobs: ConversionJob[]; known: boolean; enabled: boolean }>({
    scope: '',
    enabled: false,
    jobs: [],
    known: false,
  });
  const [launchingSourceId, setLaunchingSourceId] = useState<string | null>(null);
  // Keyed by source (GDN-3895). A refused Go is a fact about the source it was
  // pressed on; it used to be stored as one table-wide string AND to mark the
  // conversion status unknown, which blanked every other row's stage and
  // disabled Run conversion and Add files across the whole page.
  const [launchRefusals, setLaunchRefusals] = useState<Record<string, LaunchRefusal>>({});
  const sourceKey = [...new Set(rawBatchIds)].sort().join(',');
  const scope = `${organizationId}:${sourceKey}`;
  const refresh = useCallback(() => setVersion(value => value + 1), []);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const ids = sourceKey ? sourceKey.split(',') : [];
    const poll = async () => {
      const jobs: ConversionJob[] = [];
      let known = true;
      let enabled = true;
      try {
        for (let offset = 0; offset < ids.length && !cancelled; offset += 100) {
          const response = await lookup({
            organizationId,
            conversionLookupRequest: { raw_batch_ids: ids.slice(offset, offset + 100) },
          }).unwrap();
          enabled = enabled && response.enabled === true;
          jobs.push(...response.jobs);
        }
      } catch {
        known = false;
      }
      if (cancelled) return;
      setState(previous => ({
        scope,
        jobs: known ? jobs : previous.scope === scope ? previous.jobs : [],
        known,
        enabled,
      }));
      const active = jobs.some(job => job.status === 'pending' || job.status === 'running');
      if (ids.length > 0) timer = setTimeout(() => void poll(), active || !known ? 5000 : 30000);
    };
    void poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [organizationId, sourceKey, scope, version, lookup]);

  const forget = useCallback((rawBatchId: string) => {
    setLaunchRefusals(previous => {
      if (!(rawBatchId in previous)) return previous;
      const next = { ...previous };
      delete next[rawBatchId];
      return next;
    });
  }, []);

  // A refusal describes a source that has not started. Once the poll shows a
  // job for it, or the source leaves this scope entirely, the entry is no
  // longer about anything, and leaving it would pin a red caption under a row
  // that is converting happily (GDN-3895). The sibling `state` is scope-guarded
  // for the same reason; this map is pruned rather than guarded because its
  // keys are the source ids themselves.
  useEffect(() => {
    setLaunchRefusals(previous => {
      const ids = Object.keys(previous);
      if (ids.length === 0) return previous;
      const inScope = new Set(sourceKey ? sourceKey.split(',') : []);
      const started = new Set(state.jobs.map(job => job.raw_batch_id));
      const next: Record<string, LaunchRefusal> = {};
      let changed = false;
      for (const id of ids) {
        if (inScope.has(id) && !started.has(id)) next[id] = previous[id];
        else changed = true;
      }
      return changed ? next : previous;
    });
  }, [sourceKey, state.jobs]);

  const go = async (rawBatchId: string, retryOf?: string) => {
    setLaunchingSourceId(rawBatchId);
    forget(rawBatchId);
    try {
      const { job } = await launch({
        organizationId,
        rawBatchId,
        conversionLaunchRequest: retryOf ? { retry_of: retryOf } : {},
      }).unwrap();
      setState(previous => ({
        scope,
        enabled: previous.scope === scope && previous.enabled,
        known: previous.scope === scope && previous.known,
        jobs: [
          ...(previous.scope === scope
            ? previous.jobs.filter(value => value.raw_batch_id !== rawBatchId)
            : []),
          job,
        ],
      }));
    } catch (error) {
      // NOT `known: false`. That flag answers "is the conversion service
      // reachable", a fact about the table; the poll owns it. A refusal here is
      // a fact about one source, and conflating the two is what let a single
      // row's refused Go blank the stage column and disable the controls on
      // every row (GDN-3895).
      setLaunchRefusals(previous => ({ ...previous, [rawBatchId]: readLaunchRefusal(error) }));
    } finally {
      setLaunchingSourceId(null);
      refresh();
    }
  };
  return {
    jobs: state.scope === scope ? state.jobs : [],
    enabled: state.scope === scope && state.enabled,
    known: state.scope === scope && state.known,
    launchingSourceId,
    launchRefusals,
    go,
    refresh,
  };
}
